/**
 * Platform billing (MFW charges the organization for app usage) —
 * package catalog, subscription overview/setup, discount, grace, pay-now.
 *
 * ALL responses from this controller are wrapped in `{ data: ... }`.
 */
import type {
  StairToleranceResultDto,
  StairTermResultDto,
  StairChangeRowDto,
  StairOverviewDto,
  FreeAnalyticsGrantDto,
  InvoiceDetailDto,
  InvoiceDocumentDto,
  OrgCancelPlanResult,
  SubscriptionChargeDto,
  SubscriptionChargePage,
  NextPaymentPreview,
} from '@mfw/common';

import type {
  ApiPlatformPackageListItem,
  FetchPlatformPackagesArgs,
  FetchOrgInvoicesArgs,
  ApiBillingOverview,
  GrantDiscountArgs,
  ApiSubscription,
  ExtendGraceArgs,
  ApiPayNowResult,
  ApiOrgDiscount,
  OrgBillingArgs,
  ApiInvoice,
} from './types/payments.types';

import { destroy, post, get, put } from './axios';

export type FetchOrgInvoiceArgs = { organizationId: string; invoiceId: string };
export type FetchOrgInvoiceDocumentArgs = {
  organizationId: string;
  invoiceId: string;
};
export type FetchOrgChargesArgs = {
  organizationId: string;
  limit?: number;
  offset?: number;
};

function orgBillingBasePath(organizationId: string) {
  return `/v2/payments/platform/organizations/${organizationId}/billing`;
}

export const fetchPlatformPackages = async ({
  currency,
}: FetchPlatformPackagesArgs) => {
  const response = await get<{ data: ApiPlatformPackageListItem[] }>(
    '/v2/payments/platform/packages',
    { query: { currency } },
  );
  return response?.data ?? [];
};

export const fetchOrgBillingOverview = async ({
  organizationId,
}: OrgBillingArgs) => {
  const response = await get<{ data: ApiBillingOverview }>(
    orgBillingBasePath(organizationId),
  );
  return response?.data ?? null;
};

/** §4.2-A (F2.6): paginated list of organization invoices → `{ data, total }`. */
export const fetchOrgInvoices = async ({
  limit,
  offset,
  organizationId,
}: FetchOrgInvoicesArgs) => {
  const response = await get<{ data: ApiInvoice[]; total: number }>(
    `/v2/payments/platform/organizations/${organizationId}/invoices`,
    {
      query: {
        limit: limit !== undefined ? String(limit) : undefined,
        offset: offset !== undefined ? String(offset) : undefined,
      },
    },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
};

export const fetchOrgInvoice = async ({
  organizationId,
  invoiceId,
}: FetchOrgInvoiceArgs): Promise<InvoiceDetailDto> => {
  const response = await get<{ data: InvoiceDetailDto }>(
    `/v2/payments/platform/organizations/${organizationId}/invoices/${invoiceId}`,
  );
  return response?.data as InvoiceDetailDto;
};

/**
 * Q5/Task 10 — the organization invoice's PAYMENT CONFIRMATION document, as a
 * short-lived (120s) presigned URL. Fetch ONLY on click, never prefetched or
 * cached — see the sibling comment on `fetchMyInvoiceDocument`. `billing_read`
 * is required, same as the detail route beside it; a platform user reading it
 * is fine (P4/P5) — it never appears on the document as a party, since it is
 * never a payer.
 */
export const fetchOrgInvoiceDocument = async ({
  organizationId,
  invoiceId,
}: FetchOrgInvoiceDocumentArgs): Promise<InvoiceDocumentDto> => {
  const response = await get<{ data: InvoiceDocumentDto }>(
    `/v2/payments/platform/organizations/${organizationId}/invoices/${invoiceId}/document`,
  );
  return response?.data as InvoiceDocumentDto;
};

export const fetchOrgCharges = async ({
  organizationId,
  limit,
  offset,
}: FetchOrgChargesArgs): Promise<SubscriptionChargePage> => {
  const response = await get<{ data: SubscriptionChargeDto[]; total: number }>(
    `/v2/payments/platform/organizations/${organizationId}/charges`,
    {
      query: {
        limit: limit !== undefined ? String(limit) : undefined,
        offset: offset !== undefined ? String(offset) : undefined,
      },
    },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
};

export const fetchOrgNextPayment = async ({
  organizationId,
}: OrgBillingArgs): Promise<NextPaymentPreview> => {
  const response = await get<{ data: NextPaymentPreview }>(
    `${orgBillingBasePath(organizationId)}/next-payment`,
  );
  return response?.data as NextPaymentPreview;
};

/** Pricing v2: the stair ladder, where the accepted clients put the organization, the next price. */
export const fetchOrgStairs = async ({ organizationId }: OrgBillingArgs) => {
  const response = await get<{ data: StairOverviewDto }>(
    `${orgBillingBasePath(organizationId)}/stairs`,
  );
  return response?.data as StairOverviewDto;
};

export type StairTermMonths = 1 | 3 | 6 | 12;

const STAIR_TERMS: readonly number[] = [1, 3, 6, 12];

/** Narrows a billing term in months to one the term route accepts. */
export const toStairTerm = (months: number): StairTermMonths | null =>
  STAIR_TERMS.includes(months) ? (months as StairTermMonths) : null;

/** The owner picks a payment term; applies at the end of the paid period (or now when re-subscribing). */
export const chooseStairTerm = async ({
  organizationId,
  termMonths,
}: OrgBillingArgs & { termMonths: StairTermMonths }) => {
  const response = await post<StairTermResultDto>(
    `${orgBillingBasePath(organizationId)}/term`,
    { termMonths },
  );
  return response?.data ?? null;
};

/** Platform user only (non-monetary lever, like discount and grace). */
export const setOrgTolerance = async ({
  organizationId,
  tolerancePercent,
}: OrgBillingArgs & { tolerancePercent: number }) => {
  const response = await put<StairToleranceResultDto>(
    `${orgBillingBasePath(organizationId)}/tolerance`,
    { tolerancePercent },
  );
  return response?.data ?? null;
};

export type FetchStairChangesArgs = {
  tenantId?: string;
  limit: number;
  offset: number;
};

/** Platform back office: organizations whose next renewal changes stair → `{ data, total }` (not wrapped again). */
export const fetchStairChanges = async ({
  tenantId,
  limit,
  offset,
}: FetchStairChangesArgs) => {
  const response = await get<{ data: StairChangeRowDto[]; total: number }>(
    '/v2/payments/platform/stair-changes',
    {
      query: {
        tenantId,
        limit: String(limit),
        offset: String(offset),
      },
    },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
};

export const grantDiscount = async ({
  expiresAt,
  note,
  organizationId,
  percentOff,
  scope,
  startsAt,
}: GrantDiscountArgs) => {
  const response = await post<ApiOrgDiscount>(
    `${orgBillingBasePath(organizationId)}/discount`,
    { expiresAt, note, percentOff, scope, startsAt },
  );
  return response?.data ?? null;
};

export const revokeDiscount = async ({ organizationId }: OrgBillingArgs) => {
  return destroy(`${orgBillingBasePath(organizationId)}/discount`);
};

// ——— Task 7.2 — "Free analytics": a platform user's switch that gives the
// organization MyFitWorld Platform Analytics free for one month (a 100 %
// discount, never a subscription). Platform users only (P2 levers). ———

export type FreeAnalyticsArgs = { organizationId: string };
export type GrantFreeAnalyticsArgs = FreeAnalyticsArgs & { note: string };

const freeAnalyticsPath = (organizationId: string) =>
  `/v2/payments/platform/organizations/${organizationId}/premium-report/free`;

/** The live grant, or null when the switch is off (or its month ran out). */
export const fetchFreeAnalyticsGrant = async ({
  organizationId,
}: FreeAnalyticsArgs) => {
  const response = await get<{ data: FreeAnalyticsGrantDto | null }>(
    freeAnalyticsPath(organizationId),
  );
  return response?.data ?? null;
};

/** Switch on — or extend a running one — to today + 1 month; answers the grant. */
export const grantFreeAnalytics = async ({
  note,
  organizationId,
}: GrantFreeAnalyticsArgs) => {
  const response = await post<FreeAnalyticsGrantDto>(
    freeAnalyticsPath(organizationId),
    { note },
  );
  return response?.data ?? null;
};

/** Switch off now; 404 when nothing runs. */
export const endFreeAnalytics = async ({ organizationId }: FreeAnalyticsArgs) =>
  destroy(freeAnalyticsPath(organizationId));

/**
 * Platform user only; operates EXCLUSIVELY on debt (otherwise 400 with a reason).
 * The returned `graceUntil` may be shorter than requested (clamped to dueAt+30d).
 */
export const extendGrace = async ({
  days,
  organizationId,
}: ExtendGraceArgs) => {
  const response = await post<ApiSubscription>(
    `${orgBillingBasePath(organizationId)}/grace`,
    { days },
  );
  return response?.data ?? null;
};

/**
 * Self-service exit from suspension ("pay now"). The charge is ASYNCHRONOUS —
 * after the call, poll the billing overview; 409 = processing already in progress (keep polling).
 */
export const payNow = async ({ organizationId }: OrgBillingArgs) => {
  const response = await post<ApiPayNowResult>(
    `${orgBillingBasePath(organizationId)}/pay-now`,
  );
  return response?.data ?? null;
};

/**
 * Q3/Task 3: the owner stops the organization's plan. The renewal stops at the
 * end of the paid period — nothing is charged and nothing is refunded now, and
 * the organization keeps everything it has until then.
 *
 * `effectiveAt` is NEVER a moment in the past. `null` means there is no
 * scheduled moment at all — either the subscription is not on the billing
 * runner's schedule (suspended: revived only by a manual pay-now) or the
 * renewal that decides the date is committing right now.
 *
 * Refusals are machine-readable and are branched on by CODE, never by message:
 * `SUBSCRIPTION_ALREADY_ENDED` (400), `CLOSURE_SETTLEMENT_PENDING` (400),
 * `SUBSCRIPTION_STATE_CHANGED` (409, retryable).
 */
export const cancelOrgPlan = async ({ organizationId }: OrgBillingArgs) => {
  const response = await post<OrgCancelPlanResult>(
    `${orgBillingBasePath(organizationId)}/cancel`,
  );
  return response?.data ?? null;
};

/**
 * …and changes their mind. Clears the flag; the renewal then happens exactly
 * as it would have. Refuses on a subscription that has already ended and on a
 * closure settlement — coming back from a closure is choosing a plan again,
 * not un-cancelling.
 */
export const withdrawOrgPlanCancellation = async ({
  organizationId,
}: OrgBillingArgs) => {
  return destroy(`${orgBillingBasePath(organizationId)}/cancel`);
};
